import BaseTypedController from "ips/ac/base/controller/BaseTypedController";
import OrderLinkService from "ips/ac/data/order/service/OrderLinkService";
import Util from "ips/ac/util/Util";
import Dialog from "sap/m/Dialog";
import Table from "sap/m/Table";
import Select from "sap/m/Select";
import MultiComboBox from "sap/m/MultiComboBox";
import SearchField from "sap/m/SearchField";
import CheckBox from "sap/m/CheckBox";
import { ListItemBase$PressEvent } from "sap/m/ListItemBase";
import { ValueState } from "sap/ui/core/library";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import { OrderMappingEntity } from "ips/ac/model/api/WinDoFlow.api.types";

export interface LinkOrderDialogData {
  orderGuid: string;
  projectGuid: string;
  /** binding path of the order, e.g. /Orders('...') */
  orderPath: string;
}

/** MultiComboBox ids, each id is also the filtered property */
const MULTI_SELECT_FILTER_PROPERTIES = ["Type_Value", "Status_Value", "PlanningStatus_Value", "AssignedTo"];
const SEARCH_PROPERTIES = ["AssignedTo_UserFullname", "Description", "Id"];

/**
 * Dialog to link other orders to an order.
 * @namespace ips.ac.data.order.controller
 */
export default class LinkOrderDialogController extends BaseTypedController {
  private dialog: Dialog;
  private table: Table;
  private linkTypeSelect: Select;
  private data: LinkOrderDialogData;
  private excludedGuidFilter: Filter;
  private onLinked: () => void;

  public onInit(): void {
    super.onInit();
    this.dialog = this.byId("linkOrderDialog") as Dialog;
    this.table = this.byId("linkOrdersTable") as Table;
    this.linkTypeSelect = this.byId("linkTypeSelect") as Select;
  }

  public async openDialog(data: LinkOrderDialogData, onLinked: () => void): Promise<void> {
    this.data = data;
    this.onLinked = onLinked;
    this.linkTypeSelect.setValueState(ValueState.None);
    this.table.removeSelections(true);
    this.dialog.open();
    this.dialog.setBusy(true);
    try {
      this.excludedGuidFilter = await this.loadExcludedGuidFilter();
      this.onFilter();
    } finally {
      this.dialog.setBusy(false);
    }
  }

  /**
   * Excludes all orders that are already linked to the current order.
   */
  private async loadExcludedGuidFilter(): Promise<Filter | undefined> {
    const binding = Util.getODataModel().bindList(this.data.orderPath + "/OrderMappingDetails");
    try {
      const contexts = await binding.requestContexts(0, Infinity);
      const filters = contexts.map((context) => new Filter("Guid", FilterOperator.NE, context.getProperty("InstanceGuid")));
      return filters.length > 0 ? new Filter(filters, true) : undefined;
    } finally {
      binding.destroy();
    }
  }

  public onFilter(): void {
    const filters: Filter[] = [];

    MULTI_SELECT_FILTER_PROPERTIES.forEach((property) => {
      const keys = (this.byId(property) as MultiComboBox).getSelectedKeys();
      if (keys.length > 0) {
        filters.push(new Filter(keys.map((key) => new Filter(property, FilterOperator.EQ, key)), false));
      }
    });

    const searchValue = (this.byId("searchField") as SearchField).getValue();
    if (searchValue) {
      filters.push(new Filter(SEARCH_PROPERTIES.map((property) => new Filter({
        path: property,
        operator: FilterOperator.Contains,
        value1: searchValue,
        caseSensitive: false,
      })), false));
    }

    if ((this.byId("onlyWithSameObjectGuid") as CheckBox).getSelected()) {
      filters.push(new Filter("ProjectGuid", FilterOperator.EQ, this.data.projectGuid));
    }

    if (this.excludedGuidFilter) {
      filters.push(this.excludedGuidFilter);
    }
    filters.push(new Filter("Guid", FilterOperator.NE, this.data.orderGuid));

    const binding = this.table.getBinding("items") as ODataListBinding;
    binding.filter(new Filter(filters, true));
    if (binding.isSuspended()) {
      binding.resume();
    }
  }

  public onItemPress(event: ListItemBase$PressEvent): void {
    const item = event.getSource();
    item.setSelected(!item.getSelected());
  }

  public onLinkTypeChange(): void {
    this.linkTypeSelect.setValueState(ValueState.None);
  }

  public async onLink(): Promise<void> {
    const selectedItems = this.table.getSelectedItems();
    const linkType = this.linkTypeSelect.getSelectedKey();

    if (!linkType) {
      if (selectedItems.length > 0) {
        this.linkTypeSelect.setValueState(ValueState.Error);
      } else {
        this.closeDialog();
      }
      return;
    }

    const mappingEntities: OrderMappingEntity[] = selectedItems.map((item) => ({
      instanceGuid: item.getBindingContext().getProperty("Guid") as string,
      typeId: "ORDERLINK_TYPE",
      typeValue: linkType,
    }));

    this.dialog.setBusy(true);
    try {
      // HttpUtil already shows a MessageBox for error responses
      await OrderLinkService.linkOrders(this.data.orderGuid, mappingEntities);
      this.closeDialog();
      this.onLinked();
    } catch (err) {
      console.error(err);
    } finally {
      this.dialog.setBusy(false);
    }
  }

  public onCancel(): void {
    this.closeDialog();
  }

  private closeDialog(): void {
    this.dialog.close();
  }
}
